import styled, {css} from "styled-components";
import {baseDistance, ELLIPSIS, fontSize, InfoIconStyled, radius} from "../../styles/styles";
import {DropdownButtonStyled} from "../../interaction/DropdownButtonV5";
import {IconButtonStyled} from "../../interaction/IconButtonV5";
import {colorPalette} from "../../styles/colorPalette";
import GlobeIcon from "../../icons/globe.svg?react";
import StarIcon from "../../icons/star.svg?react";
import UserIcon from "../../icons/user.svg?react";
import UsersIcon from "../../icons/users.svg?react";
import type {TMenuV5} from "../NavigationCommon";
import {TextInputStyled} from "../../details/datatypes/TextInputV5";

export type PrimaryWorkspaceViewType = "Views" | "Favorites";
export type WorkspaceViewType = "Favorites" | "Global" | "My Views" | "Shared With Me";
export type OnWorkspaceRenameActiveChange = (isActive: boolean) => void;

type WorkspaceMenuV5 = TMenuV5 & {
	readonly label: WorkspaceViewType;
};

export const viewMenu: WorkspaceMenuV5[] = [
	{
		label: "Favorites",
		icon: StarIcon,
	},
	{
		label: "Global",
		icon: GlobeIcon,
	},
	{
		label: "My Views",
		icon: UserIcon,
	},
	{
		label: "Shared With Me",
		icon: UsersIcon,
	},
];

export const getKeyForLocalStorageWorkspaceViewSections = (userId: string) => `srv5_${userId}_openWorkSpaceTypes`;

const ViewElementStyledBackgroundStyle = css<{$isActive: boolean; $isHovered?: boolean}>`
	background-color: ${(props) => (props.$isActive ? colorPalette.primary.c200Light : props.$isHovered ? colorPalette.blueGray.c200Light : "initial")};
	color: ${(props) => (props.$isActive ? colorPalette.primary.c500Primary : colorPalette.gray.c950)};
	border-radius: ${radius.sm};

	&:hover {
		background-color: ${(props) => (props.$isActive ? colorPalette.primary.c200Light : colorPalette.blueGray.c200Light)};
		color: ${(props) => (props.$isActive ? colorPalette.primary.c500Primary : colorPalette.gray.c950)};

		${DropdownButtonStyled}, ${IconButtonStyled} {
			visibility: visible;
		}
	}
`;

export const ViewElementStyled = styled.div<{$isActive: boolean; $isHovered?: boolean; $iconColor?: string}>`
	display: flex;
	align-items: center;
	font-size: ${fontSize.md};
	line-height: 16px;
	height: 24px;
	cursor: pointer;
	gap: ${baseDistance.sm};
	padding: ${baseDistance.xs};
	padding-left: ${baseDistance.md};
	position: relative;
	border-radius: ${radius.sm};
	user-select: none;
	color: ${colorPalette.gray.c950};

	svg {
		min-width: 16px;
		min-height: 16px;
		max-width: 16px;
		max-height: 16px;

		// svg current color get inherited from .navIcon color property. This is to ensure the color is applied to the svg path properly.
		// Unless some icons are blurred (e.g. building.svg)
		&.navIcon {
			color: ${(props) =>
				props.$isActive ? `${colorPalette.primary.c500Primary}` : props.$iconColor ? `#${props.$iconColor}` : `${colorPalette.gray.c950}`};
		}
	}

	${DropdownButtonStyled}, ${IconButtonStyled} {
		display: flex;
		visibility: ${(props) => (props.$isActive ? "visible" : "hidden")};
		width: 24px;
		height: 24px;
		min-width: 24px;

		${IconButtonStyled} {
			width: 24px;
			height: 24px;
		}
	}

	${DropdownButtonStyled}.hover {
		visibility: visible;

		${IconButtonStyled} {
			visibility: visible;
		}
	}

	& ~ .ReactSortable:not(:has(.ViewItem)) {
		${ViewElementStyledBackgroundStyle}
	}

	${ViewElementStyledBackgroundStyle}

	&.ViewItem.level1 {
		&:hover::before,
		&.isActive::before {
			content: "";
			background-color: ${colorPalette.blueGray.c200Light};
			width: 20px;
			height: 24px;
			position: absolute;
			left: -16px;
			border-radius: ${radius.sm};
		}

		&.isActive::before {
			background-color: ${colorPalette.primary.c200Light};
		}
	}

	.viewName,
	.viewFolderName {
		${ELLIPSIS}
		min-width: 0;
	}

	${TextInputStyled} {
		height: 24px;
		width: 200px;
	}

	${InfoIconStyled} {
		top: 0px;
		right: 6px;
	}
`;
